import React, { useEffect, useState } from "react";
import { Menu, X, Github, Linkedin, Mail, Phone, Sun, Moon, ArrowUpRight } from "lucide-react";
import { useTheme } from "next-themes";
import { Button } from "./ui/button";
import { personalInfo } from "../mock";
import { cn } from "../lib/utils";

const navigationItems = [
  { name: "About", href: "#about" },
  { name: "Skills", href: "#skills" },
  { name: "Projects", href: "#projects" },
  { name: "Experience", href: "#experience" },
  { name: "Education", href: "#education" },
  { name: "Contact", href: "#contact" },
];

const socialLinks = [
  { icon: Github, href: personalInfo.github, label: "GitHub" },
  { icon: Linkedin, href: personalInfo.linkedin, label: "LinkedIn" },
  { icon: Mail, href: `mailto:${personalInfo.email}`, label: "Email" },
  { icon: Phone, href: `tel:${personalInfo.phone}`, label: "Phone" },
];

const Header = () => {
  const { theme, setTheme } = useTheme();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [active, setActive] = useState("");
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 8);
      const doc = document.documentElement;
      const total = doc.scrollHeight - window.innerHeight;
      setProgress(total > 0 ? (window.scrollY / total) * 100 : 0);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const sections = navigationItems
      .map((item) => document.querySelector(item.href))
      .filter(Boolean);

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0) setActive(`#${visible[0].target.id}`);
      },
      { rootMargin: "-40% 0px -55% 0px", threshold: 0 },
    );

    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, []);

  const scrollProgress =
    typeof window !== "undefined"
      ? (() => {
          const doc = document.documentElement;
          const total = doc.scrollHeight - window.innerHeight;
          return total > 0 ? (window.scrollY / total) * 100 : 0;
        })()
      : 0;

  return (
    <>
      <header
        className={cn(
          "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
          scrolled ? "glass shadow-md shadow-black/5" : "bg-transparent border-transparent",
        )}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative flex items-center justify-between h-12 md:h-14">
            {/* Scroll progress */}
            <div
              className="absolute bottom-0 left-0 h-[2px] rounded-full bg-gradient-to-r from-primary via-teal-400 to-emerald-400 transition-[width] duration-150"
              style={{ width: `${progress}%` }}
            />
            {/* Logo */}
            <a
              href="#home"
              onClick={() => setActive("")}
              className="cursor-target flex items-center gap-3 group flex-shrink-0"
            >
              <span className="relative grid place-items-center h-8 w-8 rounded-lg bg-gradient-to-br from-teal-500 via-emerald-500 to-teal-400 text-white font-display font-bold text-xs shadow-md shadow-teal-500/30 transition-transform duration-300 group-hover:scale-105 group-hover:rotate-3">
                AA
                <span className="absolute -inset-1 rounded-xl bg-gradient-to-br from-teal-500 to-emerald-500 opacity-40 blur-md -z-10" />
              </span>
              <span className="hidden sm:block font-display font-bold text-foreground text-sm md:text-base tracking-tight">
                {personalInfo.name}
              </span>
            </a>

            {/* Desktop nav */}
            <nav className="hidden lg:flex items-center gap-1">
              {navigationItems.map((item) => (
                <a
                  key={item.name}
                  href={item.href}
                  className={cn(
                    "cursor-target relative px-2 lg:px-2.5 py-1.5 rounded-lg text-sm font-medium transition-colors duration-200",
                    active === item.href
                      ? "text-primary"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {item.name}
                  <span
                    className={cn(
                      "absolute left-2.5 right-2.5 -bottom-0.5 h-px bg-gradient-to-r from-primary to-emerald-400 transition-opacity duration-300",
                      active === item.href ? "opacity-100" : "opacity-0",
                    )}
                  />
                </a>
              ))}
            </nav>

            {/* Actions */}
            <div className="flex items-center gap-2 md:gap-3">
              <div className="hidden lg:flex items-center">
                {socialLinks.map((social) => (
                  <a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="cursor-target p-2 text-muted-foreground hover:text-primary hover:bg-primary/10 rounded-lg transition-all duration-200"
                    aria-label={social.label}
                  >
                    <social.icon size={22} />
                  </a>
                ))}
              </div>

              <Button
                variant="ghost"
                size="icon"
                className="cursor-target relative text-muted-foreground hover:text-foreground hover:bg-secondary rounded-lg"
                onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
                aria-label="Toggle theme"
              >
                {theme === "dark" ? <Sun size={22} /> : <Moon size={22} />}
              </Button>

              <Button
                size="sm"
                className="cursor-target hidden md:inline-flex rounded-lg bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-500 hover:to-emerald-500 text-white shadow-lg shadow-teal-600/30 px-4 group"
                onClick={() =>
                  document.getElementById("contact").scrollIntoView({ behavior: "smooth" })
                }
              >
                Hire Me
                <ArrowUpRight
                  size={14}
                  className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                />
              </Button>

              <Button
                variant="ghost"
                size="icon"
                className="cursor-target lg:hidden text-foreground hover:bg-secondary rounded-lg"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                aria-label="Toggle menu"
              >
                {isMenuOpen ? <X size={20} /> : <Menu size={20} />}
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        <div
          className={cn(
            "lg:hidden overflow-hidden transition-all duration-300 glass border-t border-border",
            isMenuOpen ? "max-h-[480px] py-4" : "max-h-0 py-0",
          )}
        >
          <div className="max-w-7xl mx-auto px-4 flex flex-col gap-1.5">
            {navigationItems.map((item) => (
              <a
                key={item.name}
                href={item.href}
                onClick={() => setIsMenuOpen(false)}
                className={cn(
                  "cursor-target px-4 py-2.5 rounded-lg font-medium transition-colors",
                  active === item.href
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:text-foreground hover:bg-secondary",
                )}
              >
                {item.name}
              </a>
            ))}
            <Button
              size="sm"
              className="cursor-target mt-3 w-full rounded-lg bg-gradient-to-r from-teal-600 to-emerald-600 text-white"
              onClick={() => {
                document.getElementById("contact").scrollIntoView({ behavior: "smooth" });
                setIsMenuOpen(false);
              }}
            >
              Hire Me
            </Button>

            <div className="mt-3 flex items-center justify-center gap-2 border-t border-border/60 pt-3">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="cursor-target p-2 text-muted-foreground hover:text-primary hover:bg-primary/10 rounded-lg transition-all duration-200"
                  aria-label={social.label}
                >
                  <social.icon size={22} />
                </a>
              ))}
            </div>
          </div>
        </div>
      </header>
    </>
  );
};

export default Header;